import React, { useEffect, useState } from 'react';
import { 
    FaChevronDown, 
    FaChevronUp, 
    FaPlayCircle, 
    FaFileAlt,
    FaFilePdf,
    FaFilePowerpoint,
    FaFileVideo,
    FaFileCode,
    FaFileImage,
    FaFileAudio,
    FaFile
} from 'react-icons/fa';

const CourseModules = ({ course, modules }) => {
    const [expanded, setExpanded] = useState({});
    const [expandAll, setExpandAll] = useState(false);

    // Toggle section expand/collapse
    const toggleSection = (index) => {
        setExpanded((prev) => ({
            ...prev,
            [index]: !prev[index],
        }));
    };

    // Expand/Collapse all sections
    const toggleExpandAll = () => {
        const newExpandedState = {};
        modules?.forEach((_, index) => {
            newExpandedState[index] = !expandAll;
        });
        setExpanded(newExpandedState);
        setExpandAll(!expandAll);
    };

    // Get icon component based on content type
    const getIconComponent = (type) => {
        if (!type) return FaFile;
        
        const contentType = type.toLowerCase().trim();
        
        if (contentType === 'pdf' || contentType.includes('pdf')) {
            return FaFilePdf;
        }
        if (contentType === 'ppt' || contentType === 'pptx' || 
            contentType.includes('ppt') || contentType.includes('powerpoint')) {
            return FaFilePowerpoint;
        }
        if (contentType === 'video' || contentType === 'mp4' || 
            contentType === 'avi' || contentType === 'mov' || 
            contentType === 'wmv' || contentType.includes('video')) {
            return FaFileVideo;
        }
        if (contentType === 'image' || contentType === 'jpg' || 
            contentType === 'jpeg' || contentType === 'png' || 
            contentType === 'gif' || contentType === 'svg' || 
            contentType.includes('image')) {
            return FaFileImage;
        }
        if (contentType === 'audio' || contentType === 'mp3' || 
            contentType === 'wav' || contentType === 'aac' || 
            contentType.includes('audio')) {
            return FaFileAudio;
        }
        if (contentType === 'code' || contentType === 'html' || 
            contentType === 'css' || contentType === 'js' || 
            contentType === 'javascript' || contentType === 'python' || 
            contentType === 'java' || contentType.includes('code')) {
            return FaFileCode;
        }
        return FaFile;
    };

    // Get icon color based on content type
    const getIconColor = (type) => {
        if (!type) return '#6b7280';
        
        const contentType = type.toLowerCase().trim();
        
        if (contentType.includes('pdf')) return '#dc2626';
        if (contentType.includes('ppt') || contentType.includes('powerpoint')) return '#d97706';
        if (contentType.includes('video')) return '#2563eb';
        if (contentType.includes('image')) return '#16a34a';
        if (contentType.includes('audio')) return '#9333ea';
        if (contentType.includes('code')) return '#f59e0b';
        return '#6b7280';
    };

    // Get assessment icon component
    const getAssessmentIconComponent = (type) => {
        if (!type) return FaFileAlt;
        
        const assessmentType = type.toLowerCase().trim();
        
        if (assessmentType === 'quiz' || assessmentType.includes('quiz') || 
            assessmentType === 'test' || assessmentType.includes('test')) {
            return FaFileAlt;
        }
        if (assessmentType === 'pdf' || assessmentType.includes('pdf')) {
            return FaFilePdf;
        }
        return FaFileAlt;
    };

    // Calculate total lectures and assessments
    const totalLectures = modules?.reduce((sum, module) => sum + (module?.lessons?.length || 0), 0);
    const totalAssessments = modules?.reduce((sum, module) => sum + (module?.assessment?.length || 0), 0);

    useEffect(() => {
        console.log('Modules data:', modules);
    }, [modules]);

    // Render lecture icon
    const renderLectureIcon = (type) => {
        const IconComponent = getIconComponent(type);
        const color = getIconColor(type);
        return <IconComponent style={{ ...styles.lectureIcon, color }} />;
    };

    // Render assessment icon
    const renderAssessmentIcon = (type) => {
        const IconComponent = getAssessmentIconComponent(type);
        return <IconComponent style={{ ...styles.assessmentIcon, color: '#92400e' }} />;
    };

    return (
        <div style={styles.container}>
            <div style={styles.contentWrapper}>
                <div style={styles.header}>
                    <div>
                        <h2 style={styles.title}>Course Curriculum</h2>
                        <p style={styles.subtitle}>
                            {modules?.length || 0} modules • {totalLectures} lectures • {totalAssessments} assessments • {course?.duration || 0} Hours
                        </p>
                    </div>
                    <button 
                        onClick={toggleExpandAll} 
                        style={styles.expandAllButton}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#024076';
                            e.target.style.color = '#ffffff';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.color = '#024076';
                        }}
                    >
                        {expandAll ? 'Collapse all sections' : 'Expand all sections'}
                    </button>
                </div>

                <div style={styles.modulesContainer}>
                    {modules?.map((module, index) => (
                        <div key={index} style={styles.moduleCard}>
                            <div 
                                style={styles.moduleHeader} 
                                onClick={() => toggleSection(index)}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f9fafb';
                                }}
                            >
                                <div style={styles.moduleHeaderLeft}>
                                    <span style={styles.moduleNumber}>{index + 1}.</span>
                                    <div style={styles.moduleInfo}>
                                        <h3 style={styles.moduleTitle}>
                                            {module?.title}
                                            {module?.type && (
                                                <span style={styles.moduleType}>({module?.type})</span>
                                            )}
                                        </h3>
                                        <p style={styles.moduleStats}>
                                            {module?.lessons?.length || 0} Lectures • {module?.assessment?.length || 0} Assessment
                                        </p>
                                    </div>
                                </div>
                                <div style={styles.moduleHeaderRight}>
                                    {expanded[index] ? (
                                        <FaChevronUp style={styles.chevron} />
                                    ) : (
                                        <FaChevronDown style={styles.chevron} />
                                    )}
                                </div>
                            </div>

                            {expanded[index] && module?.lessons?.length > 0 && (
                                <div style={styles.lecturesContainer}>
                                    {module.lessons.map((lesson, i) => (
                                        <div key={i} style={styles.lectureItem}>
                                            <div style={styles.lectureLeft}>
                                                {renderLectureIcon(lesson?.contentType || lesson?.type)}
                                                <span style={styles.lectureTitle}>
                                                    {index + 1}.{i + 1} {lesson?.title}
                                                </span>
                                                {lesson?.contentType && (
                                                    <span style={styles.lectureType}>
                                                        {lesson.contentType.toUpperCase()}
                                                    </span>
                                                )}
                                            </div>
                                            {lesson?.duration && (
                                                <span style={styles.lectureDuration}>
                                                    {lesson?.duration} min
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                    
                                    {module?.assessment?.length > 0 && (
                                        <div style={styles.assessmentSection}>
                                            <div style={styles.assessmentHeader}>
                                                <span style={styles.assessmentLabel}>📝 Assessments</span>
                                            </div>
                                            {module.assessment.map((assessment, i) => (
                                                <div key={i} style={styles.assessmentItem}>
                                                    <div style={styles.assessmentLeft}>
                                                        {renderAssessmentIcon(assessment?.type)}
                                                        <span style={styles.assessmentTitle}>
                                                            {assessment?.title || `Assessment ${i + 1}`}
                                                        </span>
                                                        {assessment?.type && (
                                                            <span style={styles.assessmentType}>
                                                                {assessment.type.toUpperCase()}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {assessment?.duration && (
                                                        <span style={styles.assessmentDuration}>
                                                            {assessment.duration} min
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default CourseModules;

// CSS-in-JS styles
const styles = {
    container: {
        backgroundColor: '#ffffff',
        padding: '60px 20px',
    },
    contentWrapper: {
        maxWidth: '1400px',
        margin: '0 auto',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '32px',
        flexWrap: 'wrap',
        gap: '16px',
    },
    title: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#1f2937',
        marginBottom: '8px',
    },
    subtitle: {
        fontSize: '16px',
        color: '#6b7280',
    },
    expandAllButton: {
        backgroundColor: 'transparent',
        color: '#024076',
        border: '2px solid #024076',
        padding: '10px 20px',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s',
    },
    modulesContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    moduleCard: {
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        overflow: 'hidden',
        transition: 'box-shadow 0.2s',
    },
    moduleHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 24px',
        backgroundColor: '#f9fafb',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
    },
    moduleHeaderLeft: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '16px',
        flex: 1,
    },
    moduleNumber: {
        fontSize: '18px',
        fontWeight: '700',
        color: '#024076',
        flexShrink: 0,
    },
    moduleInfo: {
        flex: 1,
        minWidth: 0,
    },
    moduleTitle: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#1f2937',
        marginBottom: '6px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
    },
    moduleType: {
        fontSize: '14px',
        color: '#6b7280',
        fontWeight: '500',
    },
    moduleStats: {
        fontSize: '14px',
        color: '#6b7280',
        margin: 0,
    },
    moduleHeaderRight: {
        display: 'flex',
        alignItems: 'center',
        marginLeft: '16px',
    },
    chevron: {
        fontSize: '20px',
        color: '#6b7280',
        transition: 'transform 0.3s',
    },
    lecturesContainer: {
        backgroundColor: '#ffffff',
        padding: '16px 24px 16px 64px',
    },
    lectureItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px',
        marginBottom: '8px',
        borderRadius: '8px',
        transition: 'background-color 0.2s',
        cursor: 'pointer',
    },
    lectureLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flex: 1,
        minWidth: 0,
        flexWrap: 'wrap',
    },
    lectureIcon: {
        fontSize: '18px',
        flexShrink: 0,
    },
    lectureTitle: {
        fontSize: '15px',
        color: '#4b5563',
        lineHeight: '1.5',
    },
    lectureType: {
        fontSize: '11px',
        fontWeight: '600',
        color: '#6b7280',
        backgroundColor: '#f3f4f6',
        padding: '2px 8px',
        borderRadius: '4px',
        letterSpacing: '0.5px',
    },
    lectureDuration: {
        fontSize: '14px',
        color: '#9ca3af',
        flexShrink: 0,
    },
    assessmentSection: {
        marginTop: '16px',
        paddingTop: '16px',
        borderTop: '1px solid #e5e7eb',
    },
    assessmentHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '8px 16px',
        marginBottom: '8px',
    },
    assessmentLabel: {
        fontSize: '15px',
        fontWeight: '600',
        color: '#92400e',
    },
    assessmentItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 16px',
        backgroundColor: '#fef3c7',
        borderRadius: '8px',
        marginBottom: '8px',
    },
    assessmentLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flex: 1,
    },
    assessmentIcon: {
        fontSize: '18px',
        flexShrink: 0,
    },
    assessmentTitle: {
        fontSize: '14px',
        fontWeight: '500',
        color: '#92400e',
    },
    assessmentType: {
        fontSize: '11px',
        fontWeight: '600',
        color: '#92400e',
        backgroundColor: '#fde68a',
        padding: '2px 8px',
        borderRadius: '4px',
        letterSpacing: '0.5px',
    },
    assessmentDuration: {
        fontSize: '14px',
        color: '#92400e',
        flexShrink: 0,
    },
};

// Add hover styles
const styleSheet = document.createElement("style");
styleSheet.textContent = `
    .accordion-button:hover {
        background-color: #f3f4f6 !important;
    }
    
    .module-header:hover {
        background-color: #f3f4f6 !important;
    }
`;
document.head.appendChild(styleSheet);